import React, { useEffect, useState } from 'react';
import { Table, Button, Tag, Alert, Card, Input, Select, Space, Modal, Form, Switch, Avatar, Typography, Row, Col, Statistic, Checkbox, Dropdown, message, Popconfirm } from 'antd';
import { UserOutlined, SearchOutlined, EditOutlined, DeleteOutlined, BlockOutlined, CheckCircleOutlined, DownOutlined, SettingOutlined } from '@ant-design/icons';
import api from '../../../services/api';

function AdminUsers() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [editingUser, setEditingUser] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [bulkActionLoading, setBulkActionLoading] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        try {
            setLoading(true);
            const res = await api.adminListUsers({ per_page: 50, search: searchText, active: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined });
            const list = res.data?.data || res.data || [];
            setUsers(Array.isArray(list) ? list : []);
        } catch (e) {
            setError('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (user) => {
        setEditingUser(user);
        form.setFieldsValue({
            name: user.name,
            email: user.email,
            phone: user.phone,
            user_type: user.user_type,
            is_active: user.is_active
        });
        setModalVisible(true);
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            await api.adminUpdateUser(editingUser.id, values);
            setModalVisible(false);
            loadUsers();
        } catch (e) {
            console.error('Validation failed:', e);
        }
    };

    const toggleUserStatus = async (userId, currentStatus) => {
        try {
            await api.adminUpdateUser(userId, { is_active: !currentStatus });
            loadUsers();
        } catch (e) {
            setError('Failed to update user status');
        }
    };

    // Bulk Operations
    const handleBulkAction = async (action) => {
        if (selectedRowKeys.length === 0) {
            message.warning('Please select users to perform bulk action');
            return;
        }

        setBulkActionLoading(true);
        try {
            switch (action) {
                case 'activate':
                    setUsers(users.map(user => 
                        selectedRowKeys.includes(user.id) 
                            ? { ...user, is_active: true }
                            : user
                    ));
                    message.success(`${selectedRowKeys.length} users activated successfully`);
                    break;
                case 'deactivate':
                    setUsers(users.map(user => 
                        selectedRowKeys.includes(user.id) 
                            ? { ...user, is_active: false }
                            : user
                    ));
                    message.success(`${selectedRowKeys.length} users deactivated successfully`);
                    break;
                case 'delete':
                    setUsers(users.filter(user => !selectedRowKeys.includes(user.id)));
                    message.success(`${selectedRowKeys.length} users deleted successfully`);
                    break;
                default:
                    break;
            }
            setSelectedRowKeys([]);
        } catch (error) {
            message.error('Failed to perform bulk action');
        } finally {
            setBulkActionLoading(false);
        }
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedKeys) => {
            setSelectedRowKeys(selectedKeys);
        },
        onSelectAll: (selected, selectedRows, changeRows) => {
            if (selected) {
                setSelectedRowKeys(filteredUsers.map(user => user.id));
            } else {
                setSelectedRowKeys([]);
            }
        },
    };

    const bulkActionItems = [
        {
            key: 'activate',
            label: 'Activate Selected',
            icon: <CheckCircleOutlined />,
        },
        {
            key: 'deactivate',
            label: 'Deactivate Selected',
            icon: <BlockOutlined />,
        },
        {
            key: 'delete',
            label: 'Delete Selected',
            icon: <DeleteOutlined />,
            danger: true,
        },
    ];

    const filteredUsers = users.filter(user => {
        const matchesSearch = user.name.toLowerCase().includes(searchText.toLowerCase()) ||
                            user.email.toLowerCase().includes(searchText.toLowerCase());
        const matchesStatus = !statusFilter || user.is_active === (statusFilter === 'active');
        return matchesSearch && matchesStatus;
    });

    const columns = [
        {
            title: 'User',
            key: 'user',
            render: (_, user) => (
                <div className="flex items-center space-x-3">
                    <Avatar icon={<UserOutlined />} />
                    <div>
                        <div className="font-medium">{user.name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                </div>
            )
        },
        {
            title: 'Type',
            dataIndex: 'user_type',
            key: 'type',
            render: (type) => (
                <Tag color={type === 'vendor' ? 'blue' : 'green'}>
                    {type === 'vendor' ? 'Vendor' : 'Customer'}
                </Tag>
            )
        },
        {
            title: 'Phone',
            dataIndex: 'phone',
            key: 'phone'
        },
        {
            title: 'Bookings',
            dataIndex: 'total_bookings',
            key: 'bookings',
            render: (count) => count || 0
        },
        {
            title: 'Total Spent',
            dataIndex: 'total_spent',
            key: 'spent',
            render: (amount) => amount ? `₹${amount.toLocaleString()}` : '₹0'
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, user) => (
                <Tag color={user.is_active ? 'green' : 'red'}>
                    {user.is_active ? 'Active' : 'Inactive'}
                </Tag>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, user) => (
                <Space>
                    <Button 
                        type="primary" 
                        size="small" 
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(user)}
                    >
                        Edit
                    </Button>
                    <Button 
                        size="small" 
                        icon={user.is_active ? <BlockOutlined /> : <CheckCircleOutlined />}
                        onClick={() => toggleUserStatus(user.id, user.is_active)}
                    >
                        {user.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                </Space>
            )
        }
    ];

    const stats = {
        totalUsers: users.length,
        activeUsers: users.filter(u => u.is_active).length,
        vendors: users.filter(u => u.user_type === 'vendor').length,
        customers: users.filter(u => u.user_type === 'customer').length
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <Typography.Title level={2} className="!mb-0">User Management</Typography.Title>
                            <Typography.Text type="secondary">Manage user accounts and permissions</Typography.Text>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto p-6">
                {error && <Alert type="error" message={error} className="mb-6" />}

                {/* Stats */}
                <Row gutter={[16, 16]} className="mb-6">
                    <Col xs={24} sm={6}>
                        <Card className="card-compact">
                            <Statistic
                                title="Total Users"
                                value={stats.totalUsers}
                                prefix={<UserOutlined />}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={6}>
                        <Card className="card-compact">
                            <Statistic
                                title="Active Users"
                                value={stats.activeUsers}
                                prefix={<CheckCircleOutlined />}
                                valueStyle={{ color: '#52c41a' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={6}>
                        <Card className="card-compact">
                            <Statistic
                                title="Vendors"
                                value={stats.vendors}
                                prefix={<UserOutlined />}
                                valueStyle={{ color: '#722ed1' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={6}>
                        <Card className="card-compact">
                            <Statistic
                                title="Customers"
                                value={stats.customers}
                                prefix={<UserOutlined />}
                                valueStyle={{ color: '#fa8c16' }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Filters */}
                <Card className="mb-6">
                    <Row gutter={[16, 16]} align="middle">
                        <Col xs={24} sm={12} md={8}>
                            <Input
                                placeholder="Search users..."
                                prefix={<SearchOutlined />}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Select
                                placeholder="Filter by status"
                                value={statusFilter}
                                onChange={setStatusFilter}
                                style={{ width: '100%' }}
                                allowClear
                            >
                                <Select.Option value="active">Active</Select.Option>
                                <Select.Option value="inactive">Inactive</Select.Option>
                            </Select>
                        </Col>
                        <Col xs={24} md={8}>
                            {selectedRowKeys.length > 0 && (
                                <Space>
                                    <span className="text-sm text-gray-600">
                                        {selectedRowKeys.length} selected
                                    </span>
                                    <Dropdown
                                        menu={{
                                            items: bulkActionItems,
                                            onClick: ({ key }) => {
                                                if (key === 'delete') {
                                                    Modal.confirm({
                                                        title: 'Delete Selected Users',
                                                        content: `Are you sure you want to delete ${selectedRowKeys.length} users? This action cannot be undone.`,
                                                        okText: 'Delete',
                                                        okType: 'danger',
                                                        onOk: () => handleBulkAction('delete'),
                                                    });
                                                } else {
                                                    handleBulkAction(key);
                                                }
                                            },
                                        }}
                                        trigger={['click']}
                                    >
                                        <Button 
                                            icon={<SettingOutlined />} 
                                            loading={bulkActionLoading}
                                        >
                                            Bulk Actions <DownOutlined />
                                        </Button>
                                    </Dropdown>
                                </Space>
                            )}
                        </Col>
                    </Row>
                </Card>

                {/* Users Table */}
                <Card>
                    <Table
                        columns={columns}
                        dataSource={filteredUsers}
                        rowKey="id"
                        loading={loading}
                        rowSelection={rowSelection}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`
                        }}
                        scroll={{ x: true }}
                    />
                </Card>

                {/* Edit User Modal */}
                <Modal
                    title="Edit User"
                    open={modalVisible}
                    onCancel={() => setModalVisible(false)}
                    onOk={handleSave}
                    okText="Save Changes"
                >
                    <Form form={form} layout="vertical">
                        <Form.Item
                            name="name"
                            label="Full Name"
                            rules={[{ required: true, message: 'Please enter full name' }]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name="email"
                            label="Email"
                            rules={[
                                { required: true, message: 'Please enter email' },
                                { type: 'email', message: 'Please enter valid email' }
                            ]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name="phone"
                            label="Phone"
                            rules={[{ required: true, message: 'Please enter phone number' }]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name="user_type"
                            label="User Type"
                            rules={[{ required: true, message: 'Please select user type' }]}
                        >
                            <Select>
                                <Select.Option value="customer">Customer</Select.Option>
                                <Select.Option value="vendor">Vendor</Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item
                            name="is_active"
                            label="Account Status"
                            valuePropName="checked"
                        >
                            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                        </Form.Item>
                    </Form>
                </Modal>
            </div>
        </div>
    );
}

export default AdminUsers;
